import { chordLineSize, looksLikeChord, parseChordsOverLyrics } from './parse'
import { parseChordPro } from '../chordpro/parse'
import type { Notation } from '../chords/notes'
import type { Provenance, RevisionContent } from '../schema/v1'
import { normalizeSource } from '../text'
import { validateContent, type Issue } from '../validate'

/**
 * Punto de entrada de «pegar texto» (§4, vía «material tuyo»): reconoce si lo pegado es ChordPro
 * o acordes sobre la letra y devuelve la ficha con todo lo dudoso marcado.
 */
export type TextFormat = 'chordpro' | 'chords-over-lyrics'

export interface ImportOptions {
  notation?: Notation | 'auto'
  /** De dónde sale el texto (una dirección o un nombre), para la procedencia. */
  source?: string
  /** Fecha ISO. El dominio no lee el reloj: la pasa quien llama. */
  now?: string
  /** Por defecto, todo lo importado queda «por revisar» hasta que lo aceptes. */
  needsReview?: boolean
}

export interface ImportedText {
  format: TextFormat
  title?: string
  artist?: string
  content: RevisionContent
  /** Avisos del importador más las reglas musicales (acordes por revisar, anclajes…). */
  issues: Issue[]
}

const CHORDPRO_DIRECTIVE =
  /^\s*\{\s*(?:title|t|subtitle|st|artist|album|year|composer|key|capo|tempo|time|c|comment|ci|cb|start_of_\w+|end_of_\w+|so[vcbt]|eo[vcbt]|chorus|define)\b/i

/**
 * Una directiva decide ChordPro. Si no la hay, se cuentan los acordes entre corchetes y los de las
 * líneas solo de acordes, y gana el formato con más: un «[Am]» suelto en una transcripción de
 * acordes sobre la letra no la convierte en ChordPro (lo que dejaba la canción sin acordes).
 */
export function detectFormat(text: string): TextFormat {
  let bracketed = 0
  let overLyrics = 0
  for (const line of normalizeSource(text).split('\n')) {
    if (CHORDPRO_DIRECTIVE.test(line)) return 'chordpro'
    let inLine = 0
    for (const match of line.matchAll(/\[([^\]\s]+)\]/g)) {
      if (looksLikeChord(match[1]!)) inLine++
    }
    bracketed += inLine
    if (inLine === 0) overLyrics += chordLineSize(line)
  }
  return bracketed > overLyrics ? 'chordpro' : 'chords-over-lyrics'
}

export function importText(text: string, options: ImportOptions = {}): ImportedText {
  const format = detectFormat(text)
  const notation = options.notation ?? 'auto'
  const parsed =
    format === 'chordpro'
      ? parseChordPro(text, { notation })
      : { ...parseChordsOverLyrics(text, { notation }), title: undefined, artist: undefined }

  const entry: Provenance = {
    kind: 'import',
    method: format,
    needsReview: options.needsReview ?? true,
  }
  if (options.source) entry.source = options.source
  if (options.now) entry.at = options.now
  const content: RevisionContent = {
    ...parsed.content,
    provenance: { ...parsed.content.provenance, content: entry },
  }

  const issues: Issue[] = [...parsed.issues, ...validateContent(content)]
  const hasText = content.sections.some((section) => section.lines.length > 0)
  if (!hasText) {
    issues.push({
      severity: 'error',
      code: 'import.empty',
      path: '',
      message: 'No hay nada que importar: el texto está vacío',
    })
  }

  const result: ImportedText = { format, content, issues }
  if (parsed.title !== undefined) result.title = parsed.title
  if (parsed.artist !== undefined) result.artist = parsed.artist
  return result
}
